import React, { useState, useEffect, useRef } from 'react'
import './ModernSearch.scss'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../Hooks'
import { searchPageThunk, subScribePage, unsubScribePage } from '../../Redux/thunks/pageDetailThunk'
import { addPageAction, addSearchPage, setActiveSideSlug, loadPageDetail } from '../../Redux/slices/pageDetailSlice'
import { allowPostPagination, loadPostList } from '../../Redux/slices/post'
import { loading } from '../../Redux/slices/userSlice'
import LottieControl from '../lottie'
import butterflyAnimation from '../../Assets/Lottie/butterfly-animation.json'
import loaderAnimation from '../../Assets/Lottie/loader.json'
import butterfly404 from '../../Assets/Lottie/butterfly-404.json'

const ModernSearch = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [searchData, setSearchData] = useState<any>([])
  const userData = useAppSelector((state: any) => state?.user)
  const pageData = useAppSelector((state: any) => state?.pagedetail)
  const pageAction = useAppSelector((state: any) => state?.pagedetail?.pageAction)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setSearchData(pageData?.searchPage)
  }, [pageData?.searchPage])

  useEffect(() => {
    return () => {
      setSearchData([])
      dispatch(addSearchPage([]))
    }
  }, [])

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchQuery = event.target.value
    setSearch(searchQuery)
    if (searchQuery.trim().length > 2) {
      const payLoad = {
        userId: userData?.value?.data?._id,
        searchKey: searchQuery.trim(),
      }
      dispatch(addPageAction('search-started'))
      dispatch(searchPageThunk(payLoad))
    } else if (searchQuery.trim() === '') {
      setSearchData([])
      dispatch(addSearchPage([]))
    }
  }

  const handleClear = () => {
    setSearch('')
    setSearchData([])
    dispatch(addSearchPage([]))
    inputRef.current?.focus()
  }

  return (
    <div className="modern-search-root">
      <div className="modern-search-hero">
        <div className="modern-search-lottie">
          <LottieControl animationData={butterflyAnimation} />
        </div>
        <div className="modern-search-content">
          <h1 className="modern-search-title">Find a Legacy</h1>
          <p className="modern-search-subtitle">Search for people, memories, and stories. Instantly.</p>
        </div>
      </div>
      <div className={`modern-search-bar${isFocused ? ' focused' : ''}`}> 
        <i className="fa fa-search search-icon" />
        <input
          ref={inputRef}
          value={search}
          onChange={handleSearch}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder="Type to search..."
          className="modern-search-input"
        />
        {search && (
          <button className="modern-search-clear" onClick={handleClear} aria-label="Clear search">
            <i className="fa fa-times" />
          </button>
        )}
      </div>
      {userData.loading && (
        <div className="modern-search-loading">
          <LottieControl animationData={loaderAnimation} />
        </div>
      )}
      <div className="modern-search-results">
        {search.trim() && pageAction === 'search-started' && !searchData?.length && (
          <div className="modern-search-loading">
            <LottieControl animationData={loaderAnimation} />
          </div>
        )}
        {searchData?.length > 0 && search.trim() ? (
          <div className="modern-search-count">{searchData.length} {searchData.length === 1 ? 'result' : 'results'} found</div>
        ) : null}
        <div className="modern-search-grid">
          {searchData?.length && search.trim() ? (
            searchData.map((pagelist: any, index: number) => (
              <div
                key={index}
                className="modern-search-card"
                onClick={() => {
                  dispatch(setActiveSideSlug(''))
                  dispatch(loadPageDetail([]))
                  dispatch(loadPostList([]))
                  dispatch(allowPostPagination(false))
                  localStorage.setItem('fromSearch', '1')
                  navigate(`/profile/${pagelist?.unique_url}`)
                }}
              >
                <div className="modern-search-img">
                  {pagelist.image_cropped ? (
                    <img src={pagelist?.image_cropped} alt={`${pagelist?.first_name} ${pagelist?.last_name ?? ''}`} />
                  ) : (
                    <div className="modern-search-placeholder">
                      <i className="fa fa-image" />
                    </div>
                  )}
                </div>
                <div className="modern-search-info">
                  <div className="modern-search-name">{`${pagelist?.first_name} ${pagelist?.last_name ?? ''}`}</div>
                  <div className="modern-search-meta">
                    {pagelist?.privacy_status === 'PUBLIC' && (
                      <span className="privacy public"><i className="fa fa-butterflyeye" />Public</span>
                    )}
                    {pagelist?.privacy_status === 'PRIVATE' && (
                      <span className="privacy private"><i className="fa fa-butterflylock2" />Private</span>
                    )}
                    {pagelist?.privacy_status === 'HIDDEN' && (
                      <span className="privacy hidden"><i className="fa fa-butterflyeyehidden" />Hidden</span>
                    )}
                    <span className="members"><i className="fa fa-membersicon" /> {pagelist?.members?.length + 1} Members</span>
                  </div>
                  <div className="modern-search-reason">
                    {pagelist?.reason?.length > 90 ? (
                      <span>{pagelist?.reason?.toString().substr(0, 90)}...</span>
                    ) : (
                      <span>{pagelist?.reason?.toString()?.substring(0, 90)}&nbsp;</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : search.trim() ? (
            <div className="modern-search-noresult">
              <div className="modern-search-noresult-lottie">
                <LottieControl animationData={butterfly404} />
              </div>
              <div className="modern-search-noresult-text">No matching pages found</div>
              <div className="modern-search-noresult-subtext">Try different keywords or check your spelling</div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default ModernSearch 